import { createSlice } from '@reduxjs/toolkit';

const defaultListState = () => ({
  search: '',
  statusFilter: 'all',
  priorityFilter: 'all',
  assignedToFilter: 'all',
  // createdRange supports presets: 'all'|'today'|'yesterday'|'last_24hr'|'this_week'|'this_month'|'custom'
  createdRange: 'all',
  createdFrom: '',
  createdTo: '',
  productFilter: 'all',
  currentPage: 1,
  itemsPerPage: 10,
  viewMode: 'card',
  selectedTickets: [],
  bulkAssignOpen: false,
  bulkAssignee: '',
  bulkStatus: '',
});

const slice = createSlice({
  name: 'ui',
  initialState: {
    // top-level defaults for backward compatibility
    ...defaultListState(),
    // per-list keyed UI state: perList[listKey] = { ... }
    perList: {},
  },
  reducers: {
    // helpers: payload may be either a raw value (old behavior) or { listKey, value }
    setSearch(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].search = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.search = action.payload;
        state.currentPage = 1;
      }
    },
    setStatusFilter(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].statusFilter = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.statusFilter = action.payload;
        state.currentPage = 1;
      }
    },
    setPriorityFilter(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].priorityFilter = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.priorityFilter = action.payload;
        state.currentPage = 1;
      }
    },
    setProductFilter(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].productFilter = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.productFilter = action.payload;
        state.currentPage = 1;
      }
    },
    setAssignedToFilter(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].assignedToFilter = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.assignedToFilter = action.payload;
        state.currentPage = 1;
      }
    },
    setCreatedFrom(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].createdFrom = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.createdFrom = action.payload;
        state.currentPage = 1;
      }
    },
    setCreatedTo(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].createdTo = value;
        state.perList[listKey].currentPage = 1;
      } else {
        state.createdTo = action.payload;
        state.currentPage = 1;
      }
    },
    setCreatedRange(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].createdRange = value;
        // reset paging
        state.perList[listKey].currentPage = 1;
      } else {
        state.createdRange = action.payload;
        state.currentPage = 1;
      }
    },
    setCurrentPage(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].currentPage = value;
      } else {
        state.currentPage = action.payload;
      }
    },
    setViewMode(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].viewMode = value;
      } else {
        state.viewMode = action.payload;
      }
    },
    toggleSelectTicket(state, action) {
      // payload may be { listKey, id } or just id (legacy)
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, id } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        const arr = state.perList[listKey].selectedTickets || [];
        if (arr.includes(id)) state.perList[listKey].selectedTickets = arr.filter(x => x !== id);
        else state.perList[listKey].selectedTickets = [...arr, id];
      } else {
        const id = action.payload;
        if (state.selectedTickets.includes(id)) state.selectedTickets = state.selectedTickets.filter(x => x !== id);
        else state.selectedTickets.push(id);
      }
    },
    setSelectedTickets(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].selectedTickets = value;
      } else {
        state.selectedTickets = action.payload;
      }
    },
    setBulkAssignOpen(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].bulkAssignOpen = value;
      } else {
        state.bulkAssignOpen = action.payload;
      }
    },
    setBulkAssignee(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].bulkAssignee = value;
      } else {
        state.bulkAssignee = action.payload;
      }
    },
    setBulkStatus(state, action) {
      if (action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey, value } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].bulkStatus = value;
      } else {
        state.bulkStatus = action.payload;
      }
    },
    clearSelectedTickets(state, action) {
      if (action && action.payload && typeof action.payload === 'object' && 'listKey' in action.payload) {
        const { listKey } = action.payload;
        if (!state.perList[listKey]) state.perList[listKey] = defaultListState();
        state.perList[listKey].selectedTickets = [];
      } else {
        state.selectedTickets = [];
      }
    },
  },
});

export const {
  setSearch,
  setStatusFilter,
  setPriorityFilter,
  setProductFilter,
  setAssignedToFilter,
  setCreatedFrom,
  setCreatedTo,
  setCreatedRange,
  setCurrentPage,
  setViewMode,
  toggleSelectTicket,
  setSelectedTickets,
  setBulkAssignOpen,
  setBulkAssignee,
  setBulkStatus,
  clearSelectedTickets,
} = slice.actions;

export default slice.reducer;
